import { forwardRef, useEffect, useMemo, useState } from "react";
import { CardVariant } from "../../models/CardVariant";
import { autoUpdate, useFloating, useHover, useInteractions } from "@floating-ui/react";
import { CrystalWithCost } from "../../components/Crystal/CrystalWithCost";
import { Button } from "../../components/Control/Button/Button";
import { classNames, classNamesOrEmpty } from "../../style/common";
import { flip, shift } from "@floating-ui/core";
import { useIsHoverEnabled } from "../../hooks/useIsHoverEnabled";
import DeckCardCount from "./DeckCardCount";
import { TOURNAMENT_FORMAT_MAX_SERIAL_MAP, TournamentFormat } from "../../models/TournamentFormat";
import { ModernCardImage } from "../../components/CardVariantImage/ModernCardImage";
import { EXBurstIcon } from "../../components/Icons/EXBurstIcon";
import { LBIcon } from "../../components/Icons/LBIcon";
import { Price } from "../../models/Price";
import { getTcgPlayerUrl } from "../../functions/Common";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

export type DeckCardRowProps = {
    variant: CardVariant,
    variantCount: number,
    serialCount: number,
    tournamentFormat: TournamentFormat | undefined,
    allowEdit: boolean,
    variantPriceDictionary: Record<string, Price> | undefined,
    onRemoveClick?: (variant: CardVariant) => void,
    onAddClick?: (variant: CardVariant) => void,
    onContextMenuClick: (variant: CardVariant) => void,
    collection?: Record<string, number>,
    variantsBySerialDictionary: Record<string, CardVariant[]>,
    showCollectionCount?: boolean | undefined,
    hideCardPrice: boolean,
    maxCount?: number,
    showVariantInfo?: boolean,
    className?: string | undefined,
    disableHoverImage?: boolean,
    muteElements: boolean,
    key?: string | undefined
}

export function DeckCardRow(props: DeckCardRowProps) {
    const onRemoveClick = props.onRemoveClick ?? ((variant: CardVariant) => { });
    const onAddClick = props.onAddClick ?? ((variant: CardVariant) => { });
    const onContextMenuClick = props.onContextMenuClick ?? ((variant: CardVariant) => { });

    const isHoverEnabled = useIsHoverEnabled();

    const price = useMemo(() => {
        if (!props.variantPriceDictionary || !props.variant) {
            return undefined;
        }

        return props.variantPriceDictionary[props.variant.id];
    }, [props.variantPriceDictionary, props.variant]);

    return (
        <li
            key={props.variant.fullSerial}
            className={`flex justify-between gap-x-4 items-center ${classNamesOrEmpty(props.className)} select-none`}>

            {
                (isHoverEnabled && !props.disableHoverImage)
                    ? <DeckCardRowHoverBody
                        variant={props.variant}
                        showVariantInfo={props.showVariantInfo}
                        onBodyClick={onContextMenuClick}
                        price={price}
                        muteElements={props.muteElements}
                        hideCardPrice={props.hideCardPrice} />
                    : <DeckCardRowBody
                        variant={props.variant}
                        showVariantInfo={props.showVariantInfo}
                        onBodyClick={props.disableHoverImage ? () => { } : onContextMenuClick}
                        hideCardPrice={props.hideCardPrice}
                        muteElements={props.muteElements}
                        price={price} />
            }

            <div className={classNames("shrink-0 flex items-center space-x-2", props.muteElements && "opacity-30")}>
                <DeckCardCount
                    count={props.variantCount}
                    variant={props.variant}
                    variantsBySerialDictionary={props.variantsBySerialDictionary}
                    collection={props.collection}
                    showCollectionCount={props.showCollectionCount} />

                {
                    props.allowEdit && <>
                        <Button
                            color="component"
                            size="lg"
                            disabled={props.serialCount <= 0}
                            onClick={() => onRemoveClick(props.variant)}>-</Button>
                        <Button
                            color="component"
                            size="lg"
                            disabled={props.tournamentFormat && TOURNAMENT_FORMAT_MAX_SERIAL_MAP[props.tournamentFormat] != null && props.serialCount >= TOURNAMENT_FORMAT_MAX_SERIAL_MAP[props.tournamentFormat]!}
                            onClick={() => onAddClick(props.variant)}>+</Button>
                    </>
                }
            </div>
        </li>
    )
}

function DeckCardRowHoverBody(props: DeckCardRowBodyProps) {
    const [isHoverImageOpen, setIsHoverImageOpen] = useState(false);
    const { refs, floatingStyles, context } = useFloating({
        strategy: "fixed",
        open: isHoverImageOpen,
        onOpenChange: ((open, event) => {
            setIsHoverImageOpen(open);
        }),
        middleware: [shift({ padding: 10, crossAxis: true }), flip()],
        whileElementsMounted: autoUpdate
    });

    const hover = useHover(context);

    const { getReferenceProps, getFloatingProps } = useInteractions([
        hover,
    ]);

    return (
        <>
            {
                (isHoverImageOpen) &&
                <div
                    ref={refs.setFloating}
                    style={{ ...floatingStyles, pointerEvents: 'none' }}
                    {...getFloatingProps()}
                    className="max-w-[300px] w-[300px] z-50 top-0 left-0"
                >
                    <ModernCardImage
                        cardName={props.variant.card?.name}
                        imagePath={props.variant.imageId}
                        serial={props.variant.serial}
                        rounded="xl" />
                </div>
            }

            <DeckCardRowBody
                variant={props.variant}
                price={props.price}
                hideCardPrice={props.hideCardPrice}
                showVariantInfo={props.showVariantInfo}
                onBodyClick={props.onBodyClick}
                muteElements={props.muteElements}
                ref={refs.setReference}
                {...getReferenceProps()} />
        </>
    )
}

const DeckCardRowBody = forwardRef<HTMLDivElement, DeckCardRowBodyProps>((props, ref) => {
    const {
        onBodyClick,
        variant,
        price,
        showVariantInfo,
        hideCardPrice,
        muteElements,
        ...otherProps
    } = props;

    return (
        <div className={classNames("flex gap-1 items-stretch flex-1", props.muteElements && "opacity-30")}>
            <div
                ref={ref}
                className="flex min-w-0 gap-x-3 items-center flex-1"
                onClick={() => {
                    if (!onBodyClick) {
                        return;
                    }

                    onBodyClick(variant);
                }}
                {...otherProps}
            >
                {
                    (variant.card.elements) &&
                    <CrystalWithCost
                        elements={variant.card.elements}
                        cost={variant.card.cost || 0}
                        classNames={{
                            crystal: "h-10"
                        }} />

                }
                <div className="min-w-0 flex-auto">
                    <div className="flex gap-3 items-center">
                        <span className="text-sm font-semibold leading-4 text-contrast-primary">{variant.card.name}</span>
                        {
                            variant.card.isExBurst &&
                            <EXBurstIcon classNames={{ svg: "h-[14px] w-[14px] min-w-[14px] min-h-[14px] mt-[1px]" }} />
                        }
                        {
                            variant.card.isMultiPlayable &&
                            <span className={`ff-icon multicard min-h-[14px] min-w-[14px]`}></span>
                        }
                        {
                            variant.card.isLB &&
                            <LBIcon classNames={{ svg: "h-[14px] w-[14px] min-h-[14px] min-w-[14px]" }} />
                        }
                        {
                            variant.card.isSearcher &&
                            <MagnifyingGlassIcon className="h-[14px] w-[14px] min-h-[14px] min-w-[14px]" />
                        }
                    </div>
                    <div className="mt-1 flex truncate text-xs text-contrast-tertiary">
                        {
                            showVariantInfo &&
                            <>{variant.fullSerial} - {variant.variant}</>
                        }

                        {
                            !showVariantInfo &&
                            <span className="flex justify-between w-full flex-col md:flex-row">
                                {variant.fullSerial}
                                {
                                    (!!props.price && !props.hideCardPrice)
                                        ? <div>
                                            <a onClick={(event) => event.stopPropagation()} href={getTcgPlayerUrl({ finish: variant.finish, tcgPlayerId: variant.tcgPlayerId })} target="_blank" className="text-green-600 min-w-0">{`$${props.price.price.toFixed(2)}`}</a>
                                        </div>
                                        : ""
                                }
                            </span>
                        }
                    </div>
                </div>
            </div>
        </div>
    )
})

type DeckCardRowBodyProps = {
    variant: CardVariant,
    showVariantInfo?: boolean,
    price: Price | undefined,
    hideCardPrice: boolean,
    muteElements: boolean,
    onBodyClick: (variant: CardVariant) => void
}